// ----------------------------------------------------------------------

export default function Button(theme) {
  const isAmik = theme.isAmik;
  // Amik signature gradient (118° blue→teal→green) for primary actions.
  const brandGradient =
    (theme.palette.brand && theme.palette.brand.gradient) ||
    `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`;
  const brandBtnShadow =
    (theme.palette.brand && theme.palette.brand.shadowBtn) ||
    `0 4px 14px 0 ${theme.palette.primary.main}40`;
  // Amik primary button: gradient fill, lift on hover.
  const amikContained = isAmik
    ? {
        background: brandGradient,
        backgroundSize: '140% 140%',
        color: '#fff',
        boxShadow: brandBtnShadow,
        transition: 'transform .16s ease, box-shadow .16s ease, background-position .16s ease',
        '&:hover': {
          backgroundPosition: 'right center',
          boxShadow: '0 12px 26px rgba(20,120,170,.38)',
          transform: 'translateY(-1px)',
        },
        '&:active': { transform: 'translateY(0)' },
        '&.Mui-disabled': {
          background: `linear-gradient(135deg, ${theme.palette.grey[300]} 0%, ${theme.palette.grey[400]} 100%)`,
          color: theme.palette.text.disabled,
          boxShadow: 'none',
        },
      }
    : null;

  return {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: theme.shape.borderRadius * 1.5,
          textTransform: 'none',
          fontWeight: 600,
          position: 'relative',
          overflow: 'hidden',
          // Transitions removed
          '&:before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: '-100%',
            width: '100%',
            height: '100%',
            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
            // Transitions removed
          },
          '&:hover:before': {
            left: '100%',
          },
        },
        sizeLarge: {
          height: 52,
          padding: '14px 28px',
          fontSize: '16px',
          fontWeight: 700,
          letterSpacing: '0.5px',
        },
        sizeMedium: {
          height: 44,
          padding: '10px 24px',
          fontSize: '14px',
          fontWeight: 600,
          letterSpacing: '0.3px',
        },
        sizeSmall: {
          height: 36,
          padding: '8px 20px',
          fontSize: '13px',
          fontWeight: 600,
          letterSpacing: '0.2px',
        },
        containedInherit: {
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: 'white',
          boxShadow: `0 4px 14px 0 ${theme.palette.primary.main}40`,
          '&:hover': {
            background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
            boxShadow: `0 6px 20px 0 ${theme.palette.primary.main}60`,
            // Transform removed
          },
          '&:active': {
            // Transform removed
          },
          '&.Mui-disabled': {
            background: `linear-gradient(135deg, ${theme.palette.grey[300]} 0%, ${theme.palette.grey[400]} 100%)`,
            color: theme.palette.text.disabled,
            boxShadow: 'none',
          },
          ...(amikContained || {}),
        },
        containedPrimary: {
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: 'white',
          boxShadow: `0 4px 14px 0 ${theme.palette.primary.main}40`,
          '&:hover': {
            background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
            boxShadow: `0 6px 20px 0 ${theme.palette.primary.main}60`,
            // Transform removed
          },
          '&:active': {
            // Transform removed
          },
          '&.Mui-disabled': {
            background: `linear-gradient(135deg, ${theme.palette.grey[300]} 0%, ${theme.palette.grey[400]} 100%)`,
            color: theme.palette.text.disabled,
            boxShadow: 'none',
          },
          ...(amikContained || {}),
        },
        containedSecondary: {
          background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark} 100%)`,
          color: 'white',
          boxShadow: `0 4px 14px 0 ${theme.palette.secondary.main}40`,
          '&:hover': {
            background: `linear-gradient(135deg, ${theme.palette.secondary.dark} 0%, ${theme.palette.secondary.main} 100%)`,
            boxShadow: `0 6px 20px 0 ${theme.palette.secondary.main}60`,
            // Transform removed
          },
          '&:active': {
            // Transform removed
          },
          '&.Mui-disabled': {
            background: `linear-gradient(135deg, ${theme.palette.grey[300]} 0%, ${theme.palette.grey[400]} 100%)`,
            color: theme.palette.text.disabled,
            boxShadow: 'none',
          },
        },
        outlinedInherit: {
          border: `2px solid ${theme.palette.primary.main}`,
          color: theme.palette.primary.main,
          background: 'transparent',
          '&:hover': {
            background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.primary.light}10 100%)`,
            borderColor: theme.palette.primary.dark,
            color: theme.palette.primary.dark,
            // Transform removed
            boxShadow: `0 4px 12px 0 ${theme.palette.primary.main}30`,
          },
          '&:active': {
            // Transform removed
          },
        },
        outlinedPrimary: {
          border: `2px solid ${theme.palette.primary.main}`,
          color: theme.palette.primary.main,
          background: 'transparent',
          '&:hover': {
            background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.primary.light}10 100%)`,
            borderColor: theme.palette.primary.dark,
            color: theme.palette.primary.dark,
            // Transform removed
            boxShadow: `0 4px 12px 0 ${theme.palette.primary.main}30`,
          },
          '&:active': {
            // Transform removed
          },
        },
        outlinedSecondary: {
          border: `2px solid ${theme.palette.secondary.main}`,
          color: theme.palette.secondary.main,
          background: 'transparent',
          '&:hover': {
            background: `linear-gradient(135deg, ${theme.palette.secondary.main}15 0%, ${theme.palette.secondary.light}10 100%)`,
            borderColor: theme.palette.secondary.dark,
            color: theme.palette.secondary.dark,
            // Transform removed
            boxShadow: `0 4px 12px 0 ${theme.palette.secondary.main}30`,
          },
          '&:active': {
            // Transform removed
          },
        },
        textInherit: {
          color: theme.palette.primary.main,
          '&:hover': {
            background: `linear-gradient(135deg, ${theme.palette.primary.main}10 0%, ${theme.palette.primary.light}05 100%)`,
            // Transform removed
          },
        },
        textPrimary: {
          color: theme.palette.primary.main,
          '&:hover': {
            background: `linear-gradient(135deg, ${theme.palette.primary.main}10 0%, ${theme.palette.primary.light}05 100%)`,
            // Transform removed
          },
        },
        textSecondary: {
          color: theme.palette.secondary.main,
          '&:hover': {
            background: `linear-gradient(135deg, ${theme.palette.secondary.main}10 0%, ${theme.palette.secondary.light}05 100%)`,
            // Transform removed
          },
        },
      }
    }
  };
}
